import { FC, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { GetCallResponse } from '@distribute/shared/api-types/call';
import {
  checkIsMuxFailed,
  checkIsMuxLoading,
  getMuxAssetImageByPolicy,
} from '@distribute/shared/utils';

import { authUserModel } from '../../../entities/auth-user';
import {
  filterByPersonalCalls,
  filterByTeamCalls,
  useCallRecordings,
} from '../../../entities/call-recordings';
import { redirectActions } from '../../../entities/history';
import { VideoCardTablePreview } from '../../../entities/video';
import { LoaderDots } from '../../../shared/ui';
import { SearchModalEmpty } from './SearchModalEmpty';
import { SearchModalContentItem } from './SearchModalContentItem';
import { SearchModalLabel } from './SearchModalLabel';
import { SearchModalNotFound } from './SearchModalNotFound';

type Props = {
  search: string;
  onClear: () => void;
};

export const SearchModalCallRecords: FC<Props> = ({ search, onClear }) => {
  const currentUser = useSelector(authUserModel.selectors.selectUserWithError);

  const {
    data = { items: [] },
    isLoading,
    isFetching,
    isError,
  } = useCallRecordings({ search, type: 'all' });

  const { personal, team } = useMemo(() => {
    return {
      personal: filterByPersonalCalls(data.items, currentUser.id),
      team: filterByTeamCalls(data.items, currentUser.id),
    };
  }, [data.items, currentUser.id]);

  if (search.length === 0) {
    return <SearchModalEmpty />;
  }

  if (isLoading || isFetching || isError) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <LoaderDots />
      </div>
    );
  }

  if (data.items.length === 0) {
    return <SearchModalNotFound onClear={onClear} name="recordings" />;
  }

  return <Content personal={personal} team={team} />;
};

const Content = ({
  personal,
  team,
}: {
  personal: GetCallResponse[];
  team: GetCallResponse[];
}) => {
  return (
    <div className="flex flex-col gap-y-4">
      {personal.length > 0 && (
        <div className="flex flex-col gap-y-2">
          <SearchModalLabel>My calls</SearchModalLabel>
          {personal.map((call) => (
            <ContentItem key={call.id} call={call} />
          ))}
        </div>
      )}
      {team.length > 0 && (
        <div className="flex flex-col gap-y-2">
          <SearchModalLabel>Team calls</SearchModalLabel>
          {team.map((call) => (
            <ContentItem key={call.id} call={call} />
          ))}
        </div>
      )}
    </div>
  );
};

const ContentItem = ({ call }: { call: GetCallResponse }) => {
  const dispatch = useDispatch();

  return (
    <SearchModalContentItem
      onClick={() => {
        dispatch(redirectActions.toCallPage({ id: call.id }));
      }}
    >
      <VideoCardTablePreview
        isLoading={checkIsMuxLoading(call.muxAsset)}
        isFailed={checkIsMuxFailed(call.muxAsset)}
        url={getMuxAssetImageByPolicy(call.muxAsset)}
        name={call.name}
      />
    </SearchModalContentItem>
  );
};
